import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";

// Components
import { Button, CentralModal, Card, Search, DataTable, Header, Body } from "../../../../UI";
import { Heading2 } from "../../../../typography";
import ConfirmDialog from "../../../../UI/ConfirmDialog/ConfirmDialog";
import ServiceMenuItem from "../../../../lists/generalBranchSettings/ServiceMenu.item";
import AddService from "../../../../../containers/forms/generalBranchSettingsForms/ServiceMenu/AddService/AddService";
import EditService from "../../../../../containers/forms/generalBranchSettingsForms/ServiceMenu/EditService/EditService";

// Builders
import * as buttonDesignType from "../../../../../builders/buttonDesign.types";
import {
  serviceMenuFiltersBuilder,
  ServiceMenuTableHeadBuilder
} from "../../../../../builders/generalBranchSettings.builder";

// Constants

// Calls
import {
  fetchServiceMenuRequest,
  deleteServiceMenuRequest,
  exportServiceMenuPDF
} from "../../../../../api/calls/general-branch-settings.call";

// Styles
import { ServiceMenuStyles } from "./serviceMenu.styles";

const ADD_SERVICE_MENU = "ADD_SERVICE_MENU";
const EDIT_SERVICE_MENU = "EDIT_SERVICE_MENU";

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M7 1v8m0 0L4 6m3 3l3-3M2 11v1.5h10V11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServiceMenu = ({ branchId }) => {
  const [serviceMenuState, setServiceMenuState] = useState({
    serviceMenu: [],
    chosenService: undefined,
    serviceType: undefined,
    search: "",
    modalType: undefined
  });

  const [searchInput, setSearchInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const dropdownRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setServiceMenuState(prev => ({ ...prev, search: searchInput }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchServiceMenu = useCallback(async () => {
    const response = await fetchServiceMenuRequest(
      branchId,
      serviceMenuState.serviceType,
      serviceMenuState.search
    );
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    setServiceMenuState(prevValues => ({
      ...prevValues,
      serviceMenu: response.serviceMenu
    }));
  }, [branchId, serviceMenuState.serviceType, serviceMenuState.search]);

  useEffect(() => {
    fetchServiceMenu();
  }, [fetchServiceMenu]);

  const exportMenu = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportServiceMenuPDF(branchId);
      if (res.hasError) {
        toast.error(res.error?.message || "Export failed");
        return;
      }
      const fileURL = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = fileURL;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `service-menu_b${branchId}_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(fileURL);
    } finally {
      setExporting(false);
    }
  };

  const confirmDeleteService = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget;
    const response = await deleteServiceMenuRequest(branchId, targetId);
    setDeleteTarget(null);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Service item successfully deleted.");
    setDeletingId(targetId);
    setTimeout(() => {
      setDeletingId(null);
      setServiceMenuState(prevValues => ({
        ...prevValues,
        serviceMenu: response.serviceMenu
      }));
    }, 300);
  };

  const setServiceType = type => {
    setServiceMenuState(prevValues => ({
      ...prevValues,
      serviceType: type
    }));
    setDropdownOpen(false);
  };

  const setServiceMenuStateAfterCreateOrEdit = serviceMenu => {
    setServiceMenuState(prevValues => ({
      ...prevValues,
      serviceMenu,
      chosenService: undefined,
      modalType: undefined
    }));
  };

  const setModalType = type => {
    setServiceMenuState(prevValues => ({
      ...prevValues,
      modalType: type
    }));
  };

  const editService = service => {
    setServiceMenuState(prevValues => ({
      ...prevValues,
      chosenService: service,
      modalType: EDIT_SERVICE_MENU
    }));
  };

  const closeModal = () => {
    setServiceMenuState(prevValues => ({
      ...prevValues,
      chosenService: undefined,
      modalType: undefined
    }));
  };

  const selectedTypeLabel =
    serviceMenuFiltersBuilder.find(
      f => f.value === serviceMenuState.serviceType
    )?.title || "All Types";

  return (
    <ServiceMenuStyles>
      <Card rounded className="headerCard">
        <Header>
          <div className="cardHeaderRow">
            <Heading2>SERVICE MENU</Heading2>
            <Button
              onClick={() => setModalType(ADD_SERVICE_MENU)}
              className="addServiceBtn"
              buttonDesignType={buttonDesignType.PRIMARY}
            >
              Add Service Item
            </Button>
          </div>
        </Header>
        <Body>
          <div className="searchBar">
            <div className="searchContainer">
              <Search
                placeholder="Search service items"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <div className="typeDropdown" ref={dropdownRef}>
              <button
                type="button"
                className={`typeDropdownBtn ${dropdownOpen ? "open" : ""}`}
                onClick={() => setDropdownOpen(prev => !prev)}
              >
                {selectedTypeLabel}
                <span className="chevron" />
              </button>
              {dropdownOpen && (
                <div className="typeDropdownMenu">
                  {serviceMenuFiltersBuilder.map(({ value, title }, i) => (
                    <div
                      key={i}
                      className={`typeDropdownItem ${
                        serviceMenuState.serviceType === value ? "selected" : ""
                      }`}
                      onClick={() => setServiceType(value)}
                    >
                      {title}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="exportMenuBtn"
              onClick={exportMenu}
              disabled={exporting}
              title="Export the complete menu (ignores filters), grouped by service type"
            >
              <DownloadIcon />
              {exporting ? "Exporting…" : "Export Menu"}
            </button>
          </div>
        </Body>
      </Card>
      <Card rounded className="tableCard">
        <DataTable
        columns={ServiceMenuTableHeadBuilder}
        data={serviceMenuState.serviceMenu}
        emptyMessage="No service items found"
        rowClassName={item => deletingId === item.id ? "row-deleting" : ""}
        renderRow={serviceItem => (
          <ServiceMenuItem
            {...serviceItem}
            deleteAction={() => setDeleteTarget(serviceItem.id)}
            editAction={() => editService(serviceItem)}
          />
        )}
      />
      </Card>
      <CentralModal
        isOpen={!!serviceMenuState.modalType}
        closeModal={closeModal}
        width={580}
      >
        <Card>
          {serviceMenuState.modalType === ADD_SERVICE_MENU && (
            <AddService
              branchId={branchId}
              closeModal={closeModal}
              setServiceMenuStateAfterCreateOrEdit={
                setServiceMenuStateAfterCreateOrEdit
              }
            />
          )}
          {serviceMenuState.modalType === EDIT_SERVICE_MENU && (
            <EditService
              chosenService={serviceMenuState.chosenService}
              branchId={branchId}
              setServiceMenuStateAfterCreateOrEdit={
                setServiceMenuStateAfterCreateOrEdit
              }
              closeModal={closeModal}
            />
          )}
        </Card>
      </CentralModal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteService}
        title="Delete Service Item?"
        message="Are you sure you want to delete this service item?"
      />
    </ServiceMenuStyles>
  );
};

export default ServiceMenu;
